import { Component, OnInit, ViewChild, ElementRef, AfterViewChecked, ChangeDetectorRef } from '@angular/core';
import { WebServiceService } from '../../services/web-service.service';
import { HttpClient } from '@angular/common/http';
import { EesDesService } from '../../services/ees-des.service';

interface ChatMessage {
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  message_index?: number;
  isInitialGreeting?: boolean;
  feedback?: {
    isPositive: boolean;
    timestamp: Date;
  };
}

@Component({
  selector: 'app-chatbot',
  templateUrl: './chatbot.component.html',
  styleUrls: ['./chatbot.component.scss']
})
export class ChatbotComponent implements OnInit {
  isOpen = false;
  messages: ChatMessage[] = [];
  newMessage = '';
  companyThemeBlockColor: string = '';
  CompanyThemetextColor: string = '';
  isBotTyping: boolean = false;
  conversation_id: string = '0';
  ipAddress: string = '';
  inactivityTimer: any = null;
  inactivityTimeout: number =  3600000; // 1 hour
  hasAutoOpened: boolean = false;

  @ViewChild('messagesContainer') messagesContainer: ElementRef;
  @ViewChild('chatInput') chatInput: ElementRef;

  constructor(private webService: WebServiceService, private http: HttpClient, private cdr: ChangeDetectorRef, private eesdesService: EesDesService) {}

  ngOnInit() {
    // Set theme colors from company setup
    this.companyThemeBlockColor = this.webService._localStorageService.getCompanyTheme() ? this.webService._localStorageService.getCompanyTheme() : "#035168";
    this.CompanyThemetextColor = this.webService._localStorageService.getCompanyThemeColor() ? this.webService._localStorageService.getCompanyThemeColor() : "#FFFFFF";
    // Set CSS variables for chat theme globally (for button pulse)
    document.body.style.setProperty('--chatbot-theme-bg', this.companyThemeBlockColor);
    document.body.style.setProperty('--chatbot-theme-color', this.CompanyThemetextColor);
    setTimeout(() => {
      const chatWindow = document.querySelector('.chat-window') as HTMLElement;
      if (chatWindow) {
        chatWindow.style.setProperty('--chatbot-theme-bg', this.companyThemeBlockColor);
        chatWindow.style.setProperty('--chatbot-theme-color', this.CompanyThemetextColor);
      }
    }, 0);
    // Add initial greeting message
    const userName = this.webService._localStorageService.getUserName && this.webService._localStorageService.getUserName();
    const greeting = userName ? `Hello, ${userName}! How can I help you today?` : 'Hello! How can I help you today?';
    this.messages.push({
      text: greeting,
      sender: 'bot',
      timestamp: new Date(),
      isInitialGreeting: true  // Mark this as initial greeting
    });
    this.scrollToBottom();
    // Fetch IP address
    this.http.get('https://api.ipify.org/?format=json').subscribe((res: any) => {
      this.ipAddress = res.ip;
    });
    this.setupInactivityDetection();
  }

  setupInactivityDetection() {
    const resetTimer = () => {
      if (this.inactivityTimer) {
        clearTimeout(this.inactivityTimer);
      }
      if (!this.isOpen && !this.hasAutoOpened) {
        this.inactivityTimer = setTimeout(() => {
          this.isOpen = true;
          this.hasAutoOpened = true;
          this.cdr.detectChanges();
          setTimeout(() => {
            if (this.chatInput && this.chatInput.nativeElement) {
              this.chatInput.nativeElement.focus();
            }
          }, 200);
        }, this.inactivityTimeout);
      }
    };

    // List of events that indicate user activity
    const events = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll'];
    events.forEach(event => {
      window.addEventListener(event, resetTimer, true);
    });
    resetTimer(); // Start the timer initially
  }

  scrollToBottom() {
    try {
      if (this.messagesContainer) {
        this.messagesContainer.nativeElement.scrollTop = this.messagesContainer.nativeElement.scrollHeight;
      }
    } catch (err) {}
  }

  toggleChat() {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      setTimeout(() => {
        if (this.chatInput && this.chatInput.nativeElement) {
          this.chatInput.nativeElement.focus();
        }
      }, 200);
    }
  }

  sendMessage() {
    if (this.newMessage.trim()) {
      const userMsg = this.newMessage;
      this.messages.push({
        text: userMsg,
        sender: 'user',
        timestamp: new Date()
      });
      this.scrollToBottom();
      this.isBotTyping = true;
      this.cdr.detectChanges();
      this.scrollToBottom();
      this.newMessage = '';
      this.askAI(userMsg);
    }
  }

  askAI(question: string): void {
    const company_id = this.webService._localStorageService.getEmpCompanyId() || '0';
    const user_id = this.webService._localStorageService.getUserId() || '0';
    const emp_name = this.webService._localStorageService.getUserName() || '';
    const emp_code_val = this.eesdesService.get('employee_code', localStorage.getItem('employee_code'));
    const emp_code = (emp_code_val !== undefined && emp_code_val !== null && emp_code_val !== '') ? `(${emp_code_val})` : '';
    const user_name = emp_name + (emp_code ? ' ' + emp_code : '');
    const company_title = this.webService._localStorageService.getCompanyTitle ? this.webService._localStorageService.getCompanyTitle() : '';
    let data: any = {
      "company_id": company_id,
      "user_id": user_id,
      "user_name": user_name,
      "ip_address": this.ipAddress,
      "company_title": company_title,
      "question": question
    };
    if (this.conversation_id !== '0') {
      data.conversation_id = this.conversation_id;
    }
    this.http.post(this.webService.chatbotUrl + '/ask', data).subscribe(
      (res: any) => {
        let answer = (res && res.answer) ? res.answer : 'Sorry, I could not understand that.';
        if (res && res.conversation_id) {
          this.conversation_id = res.conversation_id;
        }
        this.typeBotMessage(answer);
      },
      (err) => {
        this.isBotTyping = false;
        this.messages.push({
          text: 'Sorry, there was an error connecting to the AI service.',
          sender: 'bot',
          timestamp: new Date()
        });
        this.scrollToBottom();
      }
    );
  }

  private typeBotMessage(answer: string) {
    this.isBotTyping = true;
    this.cdr.detectChanges();
    this.scrollToBottom();
    let currentText = '';
    const typingSpeed = 25; // ms per character
    
    // Calculate message index before creating the message
    const messageIndex = this.messages
      .filter(m => m.sender === 'bot' && !m.isInitialGreeting)
      .length;

    const message: ChatMessage = {
      text: '',
      sender: 'bot',
      timestamp: new Date(),
      message_index: messageIndex
    };
    
    this.messages.push(message);
    let i = 0;
    const typeChar = () => {
      if (i < answer.length) {
        currentText += answer[i];
        message.text = currentText;
        i++;
        setTimeout(typeChar, typingSpeed);
      } else {
        this.isBotTyping = false;
        this.scrollToBottom();
      }
    };
    typeChar();
  }

  isBotMessage(message: any): boolean {
    return message.sender === 'bot';
  }

  formatBotMessage(text: string): string {
    if (!text) return '';
    return text.replace(/\n/g, '<br>');
  }

  formatTimestamp(date: Date): string {
    if (!date) return '';
    const d = new Date(date);
    let hours = d.getHours();
    const minutes = d.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    return `${hours}:${minutes} ${ampm}`;
  }

  get hasConversation(): boolean {
    // Check if there are messages beyond the initial greeting
    // Initial greeting is at index 0, so we need at least 2 messages for a conversation
    return this.messages.length > 1 && this.conversation_id !== '0';
  }

  downloadConversation() {
    if (!this.hasConversation) {
      return;
    }
    
    // Direct download using window.open
    window.open(`${this.webService.chatbotUrl}/download-conversation/${this.conversation_id}`, '_blank');
  }

  submitFeedback(message: ChatMessage, isPositive: boolean) {
    console.log('Submitting feedback:', { message, isPositive }); // Debug log

    // Skip if it's the initial greeting or no conversation ID
    if (message.isInitialGreeting || this.conversation_id === '0') {
      return;
    }

    // Ensure message_index exists
    let messageIndex = message.message_index;
    if (messageIndex === undefined) {
      messageIndex = this.messages
        .filter(m => m.sender === 'bot' && !m.isInitialGreeting)
        .findIndex(m => m === message);
    }

    if (messageIndex === -1) {
      console.error('Could not determine message index');
      return;
    }

    const feedbackData = {
      conversation_id: this.conversation_id,
      message_index: messageIndex,
      is_helpful: isPositive
    };

    console.log('Sending feedback data:', feedbackData); // Debug log

    this.http.post(this.webService.chatbotUrl + '/feedback', feedbackData)
      .subscribe(
        (response) => {
          console.log('Feedback submitted successfully:', response); // Debug log
          message.feedback = {
            isPositive: isPositive,
            timestamp: new Date()
          };
          this.cdr.detectChanges(); // Ensure UI updates
        },
        (error) => {
          console.error('Error submitting feedback:', error);
        }
      );
  }

  isInitialMessage(message: ChatMessage): boolean {
    return message.isInitialGreeting === true;
  }
} 


// WEBPACK FOOTER //
// /codebuild/output/src2327239889/src/src/app/shared/components/chatbot/chatbot.component.ts